import { z } from 'zod';

// One pregenerated one-time code and the position it occupies in its table. `code` is a STRING and
// never a number: the reset codes are zero-filled to 8 digits (`str(n).zfill(8)`) and the 4-digit
// opening codes are generated over the full digit alphabet, so leading zeros are frequent and
// load-bearing — a code re-typed without them does not open the «buzón». Codes are opening
// credentials: they are rendered, never logged, never sent to analytics.
const LockCodeEntrySchema = z.object({
  code: z.string(),
  position: z.number().int().nonnegative(),
});

// A «carril»: the window of the code table around the server pointer, as devices-service fixed it
// (10 previous · the current one · 5 next, so up to 16 entries; fewer near position 0, where the
// walk back stops instead of wrapping — that short window is exactly what lets the UI disable the
// slide control at the edge). `sequence` is in ascending position order and INCLUDES the current
// position. `current_position` is the position of the NEXT code to give, not the last one given.
const LockCodeRailSchema = z
  .object({
    sequence: z.array(LockCodeEntrySchema),
    current_position: z.number().int().nonnegative(),
  })
  .transform((rail) => ({
    sequence: rail.sequence,
    currentPosition: rail.current_position,
  }));

// The two code tables of a cerradura, read lock-first. A table with no codes comes back as a NULL
// carril (not an object with an empty list) — the other one must keep working, so each carril is
// independent. An ABSENT carril is normalized to null for the same reason: the block degrades to
// «No hay códigos disponibles» on that carril rather than failing the whole read.
//
// `bypass` is deliberately NOT modelled. It is the escalation code, app-service does not forward it
// (CBX-10987), and Zod's default strip keeps it out of the domain object if that ever changes — the
// panel paints two carriles and has no business holding a third set of opening credentials.
export const LockCodesSchema = z.object({
  open: LockCodeRailSchema.nullish().transform((rail) => rail ?? null),
  reset: LockCodeRailSchema.nullish().transform((rail) => rail ?? null),
});

export type LockCodes = z.infer<typeof LockCodesSchema>;
export type LockCodeRail = NonNullable<LockCodes['open']>;
export type LockCodeEntry = LockCodeRail['sequence'][number];
